import React, { useEffect, useState } from 'react';
import {
  Title,
  Text,
  Label,
  Input,
  TextArea,
  Button,
  FileUploader,
  MessageStrip,
  Icon,
} from '@ui5/webcomponents-react';
import useAutoTranslate from '../../../hooks/useAutoTranslate';
import { CONFIG_STYLES } from '../configStyles';

function CompanyConfig({ formData, setFormData, systemSettings, onSave, isLoading }) {
  const [selectedFile, setSelectedFile] = useState(null);
  const { autoT } = useAutoTranslate();

  useEffect(() => {
    console.log('CompanyConfig - Actualización de datos:', { formData, systemSettings });
  }, [formData, systemSettings]);

  if (!systemSettings || !formData) {
    return <div>Error: Faltan datos necesarios para la configuración de la empresa</div>;
  }

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const validTypes = ['image/png', 'image/jpeg', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      alert('Formato de archivo no soportado. Use PNG, JPG o SVG.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      alert('El archivo excede el tamaño máximo de 2MB.');
      return;
    }
    setSelectedFile(file);
    handleInputChange('logo', file);
  };

  const prepareDataForSave = () => {
    const updatedSettings = {
      ...systemSettings,
      rut:     { ...systemSettings.rut,     default: formData.rut     || systemSettings.rut.default },
      name:    { ...systemSettings.name,    default: formData.name    || systemSettings.name.default },
      email:   { ...systemSettings.email,   default: formData.email   || systemSettings.email.default },
      phone:   { ...systemSettings.phone,   default: formData.phone   || systemSettings.phone.default },
      address: { ...systemSettings.address, default: formData.address || systemSettings.address.default },
      website: { ...systemSettings.website, default: formData.website || systemSettings.website.default },
    };
    if (selectedFile) updatedSettings.logo = { ...systemSettings.logo, file: selectedFile };
    return updatedSettings;
  };

  const handleSave = () => onSave(prepareDataForSave());

  const hasChanges = () =>
    formData.rut     !== systemSettings.rut.default     ||
    formData.name    !== systemSettings.name.default    ||
    formData.email   !== systemSettings.email.default   ||
    formData.phone   !== systemSettings.phone.default   ||
    formData.address !== systemSettings.address.default ||
    formData.website !== systemSettings.website.default ||
    selectedFile !== null;

  return (
    <div style={CONFIG_STYLES.page}>
      {/* Header */}
      <div style={CONFIG_STYLES.headerRow}>
        <div style={CONFIG_STYLES.headerTitle}>
          <Title level="H3">{autoT('Información de la Empresa')}</Title>
          <Text style={CONFIG_STYLES.labelHint}>{autoT('Datos corporativos y branding')}</Text>
        </div>
        <Button
          design="Emphasized"
          onClick={handleSave}
          disabled={!hasChanges() || isLoading}
          icon="save"
        >
          {isLoading ? autoT('Guardando...') : autoT('Guardar Cambios')}
        </Button>
      </div>

      {hasChanges() && (
        <MessageStrip design="Information" style={{ marginBottom: '1rem' }}>
          {autoT('Hay cambios pendientes de guardar')}
        </MessageStrip>
      )}

      {/* Datos principales */}
      <div style={CONFIG_STYLES.sectionCard}>
        <div style={CONFIG_STYLES.sectionDivider}>
          <Title level="H4">{autoT('Datos Corporativos')}</Title>
        </div>
        <div style={CONFIG_STYLES.formGrid}>
          <div style={CONFIG_STYLES.fieldGroup}>
            <Label required={systemSettings.name.required}>{autoT('Nombre de la Empresa')}</Label>
            <Input
              value={formData.name || ''}
              style={{ width: '100%' }}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder={`Ej: ${systemSettings.name.default}`}
            />
          </div>

          <div style={CONFIG_STYLES.fieldGroup}>
            <Label>{autoT('RUT / Tax ID')}</Label>
            <Input
              value={formData.rut || ''}
              style={{ width: '100%' }}
              onChange={(e) => handleInputChange('rut', e.target.value)}
              placeholder={`Ej: ${systemSettings.rut.default}`}
            />
          </div>

          <div style={CONFIG_STYLES.fieldGroup}>
            <Label>{autoT('Teléfono')}</Label>
            <Input
              value={formData.phone || ''}
              style={{ width: '100%' }}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              placeholder={`Ej: ${systemSettings.phone.default}`}
            />
          </div>

          <div style={CONFIG_STYLES.fieldGroup}>
            <Label>{autoT('Email Corporativo')}</Label>
            <Input
              type="Email"
              value={formData.email || ''}
              style={{ width: '100%' }}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder={`Ej: ${systemSettings.email.default}`}
            />
          </div>

          <div style={CONFIG_STYLES.fieldGroup}>
            <Label>{autoT('Sitio Web')}</Label>
            <Input
              value={formData.website || ''}
              style={{ width: '100%' }}
              onChange={(e) => handleInputChange('website', e.target.value)}
              placeholder={`Ej: ${systemSettings.website.default}`}
            />
          </div>

          <div style={CONFIG_STYLES.fieldGroup}>
            <Label>{autoT('Logo de la Empresa')}</Label>
            <FileUploader accept=".png,.jpg,.svg" onChange={handleFileChange} hideInput>
              <Button icon="upload" design="Transparent" style={{ width: '100%' }}>
                {autoT('Seleccionar archivo')}
              </Button>
            </FileUploader>
            <Text style={CONFIG_STYLES.labelHint}>
              {autoT('Formatos soportados:')} {systemSettings.logo.supportedFormats.join(', ')} — {autoT('Máx.')} {systemSettings.logo.maxSize}
            </Text>
          </div>

          <div style={{ ...CONFIG_STYLES.fieldGroup, gridColumn: 'span 2' }}>
            <Label>{autoT('Dirección')}</Label>
            <TextArea
              value={formData.address || ''}
              onChange={(e) => handleInputChange('address', e.target.value)}
              placeholder={`Ej: ${systemSettings.address.default}`}
              style={{ width: '100%', minHeight: '4.5rem' }}
            />
          </div>
        </div>
      </div>

      {/* Vista Previa */}
      <div style={CONFIG_STYLES.infoBox}>
        <div style={CONFIG_STYLES.infoHeader}>
          <Icon name="show" style={{ color: 'var(--sapHighlightColor)' }} />
          <Title level="H5">{autoT('Vista Previa del Perfil')}</Title>
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          padding: '1rem',
          background: 'var(--sapBackgroundColor)',
          borderRadius: '0.375rem',
          flexWrap: 'wrap',
        }}>
          <div style={{
            width: '64px',
            height: '64px',
            background: 'var(--sapNeutralBackground)',
            borderRadius: '0.375rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
            {selectedFile ? (
              <img
                src={URL.createObjectURL(selectedFile)}
                alt="Logo"
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
              />
            ) : (
              <Icon name="building" style={{ fontSize: '2rem', color: 'var(--sapContent_NonInteractiveIconColor)' }} />
            )}
          </div>
          <div>
            <Title level="H4" style={{ margin: 0 }}>
              {formData.name || systemSettings.name.default}
            </Title>
            <Text style={CONFIG_STYLES.labelHint}>RUT: {formData.rut || systemSettings.rut.default}</Text>
            <br />
            <Text style={CONFIG_STYLES.labelHint}>{formData.email || systemSettings.email.default}</Text>
            <br />
            <Text style={{ ...CONFIG_STYLES.labelHint, color: 'var(--sapLinkColor)', textDecoration: 'underline' }}>
              {formData.website || systemSettings.website.default}
            </Text>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CompanyConfig;
